import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  AdminSetStoreSearchKeywordsMutation,
  AdminSetStoreSearchKeywordsMutationVariables,
  AdminStoreSearchKeywordsQuery,
  AdminStoreSearchKeywordsQueryVariables,
  StoreSearchKeywordStoreListQuery,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";
import { AdminListDetailLayout } from "../components/admin-list-detail-layout";
import { AdminStoreTableWithMobileCards } from "../components/admin-store-table-with-mobile-cards";
import { useScrollToDetailPanelOnSelect } from "../hooks/use-scroll-to-detail-panel";

const STORE_SEARCH_KEYWORD_STORE_LIST = gql(`
  query StoreSearchKeywordStoreList {
    getStoreList {
      ok
      error
      result {
        id
        title
        address
      }
    }
  }
`);

const ADMIN_STORE_SEARCH_KEYWORDS = gql(`
  query AdminStoreSearchKeywords($storeId: Int!) {
    adminStoreSearchKeywords(storeId: $storeId) {
      id
      keyword
    }
  }
`);

const ADMIN_SET_STORE_SEARCH_KEYWORDS = gql(`
  mutation AdminSetStoreSearchKeywords($storeId: Int!, $keywords: [String!]!) {
    adminSetStoreSearchKeywords(storeId: $storeId, keywords: $keywords) {
      ok
      error
    }
  }
`);

type StoreRow = NonNullable<
  StoreSearchKeywordStoreListQuery["getStoreList"]
>["result"][number];

export default function AdminStoreSearchKeyword() {
  const [selectedStore, setSelectedStore] = useState<StoreRow | null>(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);

  useScrollToDetailPanelOnSelect(selectedStore);

  const { data, loading, error, refetch } = useQuery<StoreSearchKeywordStoreListQuery>(
    STORE_SEARCH_KEYWORD_STORE_LIST,
  );

  const {
    data: keywordData,
    loading: keywordLoading,
    refetch: refetchKeywords,
  } = useQuery<AdminStoreSearchKeywordsQuery, AdminStoreSearchKeywordsQueryVariables>(
    ADMIN_STORE_SEARCH_KEYWORDS,
    {
      variables: { storeId: selectedStore?.id ?? 0 },
      skip: !selectedStore,
      fetchPolicy: "network-only",
    },
  );

  const [adminSetStoreSearchKeywords, { loading: saving }] = useMutation<
    AdminSetStoreSearchKeywordsMutation,
    AdminSetStoreSearchKeywordsMutationVariables
  >(ADMIN_SET_STORE_SEARCH_KEYWORDS, {
    onCompleted: (res) => {
      if (res.adminSetStoreSearchKeywords.ok) {
        notify("검색 키워드가 저장되었습니다.");
        refetchKeywords();
      } else {
        notifyError(
          res.adminSetStoreSearchKeywords.error ?? "저장에 실패했습니다.",
        );
      }
    },
    onError: (e) => {
      notifyError(e.message);
    },
  });

  useEffect(() => {
    if (!selectedStore) {
      setKeywords([]);
      setKeywordInput("");
      return;
    }

    const savedKeywords =
      keywordData?.adminStoreSearchKeywords?.map((item) => item.keyword) ?? [];
    setKeywords(savedKeywords);
    setKeywordInput("");
  }, [selectedStore, keywordData]);

  const fullList = data?.getStoreList?.ok ? data.getStoreList.result ?? [] : [];
  const filterKeyword = searchKeyword.trim().toLowerCase();
  const list = filterKeyword
    ? fullList.filter(
        (store) =>
          store.title.toLowerCase().includes(filterKeyword) ||
          (store.address?.toLowerCase().includes(filterKeyword) ?? false),
      )
    : fullList;

  const handleAddKeyword = () => {
    const nextKeyword = keywordInput.trim();
    if (!nextKeyword) {
      notifyError("키워드를 입력해주세요.");
      return;
    }

    const isDuplicate = keywords.some(
      (keyword) => keyword.toLowerCase() === nextKeyword.toLowerCase(),
    );
    if (isDuplicate) {
      notifyError("이미 추가된 키워드입니다.");
      return;
    }

    setKeywords((prev) => [...prev, nextKeyword].sort((a, b) => a.localeCompare(b, "ko")));
    setKeywordInput("");
  };

  const handleRemoveKeyword = (targetKeyword: string) => {
    setKeywords((prev) => prev.filter((keyword) => keyword !== targetKeyword));
  };

  const handleSaveKeywords = () => {
    if (!selectedStore) {
      notifyError("매장을 선택해주세요.");
      return;
    }

    adminSetStoreSearchKeywords({
      variables: {
        storeId: selectedStore.id,
        keywords,
      },
    });
  };

  const handleSelectStore = (store: StoreRow) => {
    setSelectedStore(store);
  };

  if (loading) {
    return (
      <div className="w-full p-4">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">에러: {error.message}</div>
      </div>
    );
  }

  if (!data?.getStoreList?.ok) {
    return (
      <div className="w-full p-4">
        <div className="text-red-600">
          {data?.getStoreList?.error ?? "목록을 불러올 수 없습니다."}
        </div>
      </div>
    );
  }

  return (
    <AdminListDetailLayout
      title="매장 검색 키워드 관리"
      description="매장을 선택한 뒤 검색에 노출될 키워드를 추가하세요. 예: 천원의 초밥 매장에 '스시', '초밥' 키워드를 등록하면 '스시' 검색 시 노출됩니다."
      leftPanel={{
        title: "매장 목록",
        searchPlaceholder: "매장명, 주소로 검색",
        searchValue: searchKeyword,
        onSearchChange: setSearchKeyword,
        children: (
          <AdminStoreTableWithMobileCards
            stores={list.map((store) => ({
              id: store.id,
              title: store.title,
              address: store.address ?? undefined,
            }))}
            selectionMode="single"
            selectedIds={selectedStore ? [selectedStore.id] : []}
            onSelect={(store) => {
              const full = list.find((item) => item.id === store.id);
              if (full) handleSelectStore(full);
            }}
            showImage={false}
            emptyText={
              filterKeyword
                ? "검색 결과가 없습니다."
                : "등록된 매장이 없습니다."
            }
          />
        ),
      }}
      rightPanel={{
        title: "검색 키워드",
        emptyText: "왼쪽 목록에서 매장을 선택하세요.",
        children: selectedStore ? (
          <>
            <div className="text-sm text-gray-700">
              <span className="font-medium">선택 매장:</span>{" "}
              {selectedStore.title} (ID: {selectedStore.id})
            </div>

            {keywordLoading ? (
              <div className="text-sm text-gray-500">키워드 불러오는 중...</div>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    키워드 추가
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      placeholder="예: 스시, 초밥, 일식"
                      className="flex-1 h-11 px-3 rounded-lg border border-gray-300 focus:outline-none focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={handleAddKeyword}
                      className="h-11 px-4 rounded-lg bg-gray-100 text-gray-800 font-medium active:bg-gray-200"
                    >
                      추가
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-700">
                    등록된 키워드 ({keywords.length})
                  </div>
                  {keywords.length === 0 ? (
                    <div className="text-sm text-gray-500">
                      등록된 키워드가 없습니다.
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {keywords.map((keyword) => (
                        <div
                          key={keyword}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-sm text-gray-800"
                        >
                          <span>{keyword}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveKeyword(keyword)}
                            className="text-gray-500 hover:text-red-600"
                            aria-label={`${keyword} 삭제`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleSaveKeywords}
                    disabled={saving}
                    className="h-11 px-5 rounded-lg bg-primary text-white font-medium disabled:opacity-50"
                  >
                    {saving ? "저장 중..." : "저장"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      refetch();
                      refetchKeywords();
                    }}
                    className="h-11 px-5 rounded-lg border border-gray-300 text-gray-700 font-medium"
                  >
                    새로고침
                  </button>
                </div>
              </>
            )}
          </>
        ) : null,
      }}
    />
  );
}
